'use client'

import { IIconProps, Icon } from '@/common/components/Icon'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Label } from '@/components/ui/label'
import { cn } from '@/lib/utils'
import { Loader } from 'lucide-react'
import { forwardRef } from 'react'

export interface SelectOption {
  value: string
  label: string | React.ReactNode
  textValue?: string
  disabled?: boolean
}

export interface SelectOptionGroup {
  label: string
  options: SelectOption[]
}

interface SelectFieldProps {
  label?: string | React.ReactNode
  required?: boolean
  outline?: boolean
  iconBefore?: IIconProps
  iconAfter?: IIconProps
  error?: string
  loading?: boolean
  wrapperClassName?: string
  labelClassName?: string
  triggerClassName?: string
  contentClassName?: string
  placeholder?: string
  options?: SelectOption[]
  optionGroups?: SelectOptionGroup[]
  value?: string
  onValueChange?: (value: string) => void
  disabled?: boolean
  name?: string
}

const SelectField = forwardRef<HTMLButtonElement, SelectFieldProps>(
  (
    {
      label,
      required,
      outline,
      iconBefore,
      iconAfter,
      error,
      loading,
      wrapperClassName,
      labelClassName,
      triggerClassName,
      contentClassName,
      placeholder,
      options,
      optionGroups,
      value,
      onValueChange,
      disabled,
      name,
    }: SelectFieldProps,
    ref,
  ) => {
    return (
      <div className={cn('grid w-full gap-1', wrapperClassName)}>
        {label && (
          <Label
            className={cn('text-body-medium text-monochrome-1', labelClassName)}
          >
            {label}
            {required && <span className="text-red-500">*</span>}
          </Label>
        )}
        <div className="relative">
          {iconBefore && (
            <Icon
              className={cn(
                'absolute left-4 z-10 pointer-events-none',
                outline ? 'top-1/2 -translate-y-1/2' : 'h-[36px]',
                iconBefore.className,
              )}
              icon={iconBefore.icon}
              width={iconBefore.width}
              height={iconBefore.height}
              viewBox={iconBefore.viewBox}
            />
          )}
          <Select
            value={value}
            onValueChange={onValueChange}
            disabled={disabled || loading}
            name={name}
          >
            <SelectTrigger
              ref={ref}
              className={cn(
                '!bg-transparent border-x-0 border-t-0 backdrop-blur-xl border-b-monochrome-2/40 rounded-none focus:ring-0 focus:ring-offset-0 data-[placeholder]:font-normal data-[placeholder]:text-monochrome-2 text-monochrome-1 text-[11px] leading-[14px] font-medium h-[36px] px-0 pb-3 pt-0',
                outline &&
                  '!bg-monochrome-3-60 rounded-[30px] !border border-monochrome-120 px-4 py-2',
                iconBefore && 'ps-10',
                iconAfter && 'pe-10',
                error && 'border-accent-3 focus:border-accent-3',
                triggerClassName,
              )}
            >
              <SelectValue
                placeholder={placeholder}
                className="[data-placeholder]:font-semibold [data-placeholder]:text-base placeholder:text-monochrome-2"
              />
            </SelectTrigger>
            <SelectContent
              className={cn(
                'bg-monochrome-4-60-solid border-monochrome-120 max-h-[400px] overflow-y-auto',
                contentClassName,
              )}
            >
              {optionGroups ? (
                optionGroups.map((group, groupIndex) => (
                  <SelectGroup key={`group-${groupIndex}`} className="w-full">
                    <SelectLabel>{group.label}</SelectLabel>
                    {group.options.map((option) => (
                      <SelectItem
                        key={option.value}
                        value={option.value}
                        disabled={option.disabled}
                        textValue={option.textValue}
                      >
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectGroup>
                ))
              ) : (
                <SelectGroup className="w-full">
                  {options?.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      disabled={option.disabled}
                      textValue={option.textValue}
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              )}
            </SelectContent>
          </Select>
          {loading && (
            <div
              className={cn(
                'absolute right-4 pointer-events-none',
                outline ? 'top-1/2 -translate-y-1/2' : 'top-[6px]',
              )}
            >
              <Loader className="animate-spin origin-center text-monochrome1/60" />
            </div>
          )}
          {iconAfter && !loading && (
            <Icon
              className={cn(
                'absolute right-4 pointer-events-none',
                outline ? 'top-1/2 -translate-y-1/2' : 'top-[6px]',
                iconAfter.className,
              )}
              icon={iconAfter.icon}
              width={iconAfter.width}
              height={iconAfter.height}
              viewBox={iconAfter.viewBox}
            />
          )}
        </div>
        {error && <span className="text-xs text-accent-3 mt-1">{error}</span>}
      </div>
    )
  },
)

SelectField.displayName = 'SelectField'

export default SelectField
